'use client';
// Registers the <iconify-icon> custom element used by generated component
// HTML (see app/ai/templates/global-project/globalComponents/Icon.jsx and the
// LLM prompt in app/api/openai/context/getHtmlCss.ts). Required for icons to
// render in user designs — do not remove.
import '@iconify-icon/react';
import {
  lazy,
  memo,
  Suspense,
  useContext,
  useEffect,
  useRef,
  useState,
  useTransition,
} from 'react';
import {useAiThinkingForChat} from '@features/ai/useAiThinking';
import {useClarifyingQuestions} from '@features/ai/streaming/useGeneration';
import type {GenType} from '@features/flow/flowService';
import {WebHtmlCssFrame} from './components/WebHtmlCssFrame';
import {cn} from '@features/ui/utils';
import {ReadonlyContext} from '@features/contexts/ReadonlyContext';
import {useActiveStructure} from './frameHook';
import {getNodeDimensions} from '@features/flow/utils/nodeDimensions';
import {SelectionOverlays} from './components/SelectionOverlays';
import {useAtomValue} from 'jotai';
import {interactionModeAtom} from '@/app/flow/components/InteractionMode';

const WebFrame = lazy(() =>
  import('./components/WebFrame').then((a) => ({default: a.WebFrame})),
);

interface FrameProps {
  chatId: string;
  flowId: string;
  isActiveFrame?: boolean;
  isSelected?: boolean;
  genType: GenType;
  hasPartialSelection?: boolean;
  isInSelectMode?: boolean;
  isScreenInfoHovered?: boolean;
  isFrameNodeHovered?: boolean;
}

const FrameComponent = ({
  chatId,
  flowId,
  isActiveFrame = false,
  isSelected = false,
  genType,
  hasPartialSelection = false,
  isInSelectMode = false,
  isScreenInfoHovered = false,
  isFrameNodeHovered = false,
}: FrameProps) => {
  const {readonly} = useContext(ReadonlyContext);
  const {activeStructure, isFetched: contentReady} = useActiveStructure(chatId);
  const isAiThinkingForChat = useAiThinkingForChat(chatId);
  const hasClarifyingQuestions = useClarifyingQuestions(chatId) !== null;
  const mode = useAtomValue(interactionModeAtom);

  // Gate content display on data readiness.
  // false→true is deferred via startTransition (mounting heavy content).
  // true→false is synchronous to avoid rendering stale content for an uncached screen.
  const [showContent, setShowContent] = useState(contentReady);
  const [, startTransition] = useTransition();

  if (!contentReady && showContent) {
    setShowContent(false);
  }

  useEffect(() => {
    startTransition(() => setShowContent(contentReady));
  }, [contentReady, startTransition]);

  // When the screen-name area is hovered while an inner element is selected,
  // surface a 2px primary outline so the screen acts as a click target to
  // return selection to itself.
  const outlineStyle =
    hasPartialSelection && isScreenInfoHovered && !isAiThinkingForChat
      ? 'calc(2px * var(--inverse-zoom, 1)) solid var(--primary-hex)'
      : getOutlineStyle(
          readonly,
          isAiThinkingForChat,
          hasClarifyingQuestions,
          hasPartialSelection,
          isActiveFrame,
          isSelected,
        );

  const overlayContainerRef = useRef<HTMLDivElement>(null);

  if (!showContent) {
    return (
      <SuspendedFrame
        genType={genType}
        outlineStyle={outlineStyle}
        isAiThinking={isAiThinkingForChat}
        hasClarifyingQuestions={hasClarifyingQuestions}
      />
    );
  }

  return (
    <div className='rounded-[32px]'>
      <div
        ref={overlayContainerRef}
        data-zoom-chrome='frame-outline'
        style={{
          overflow: 'hidden',
          position: 'relative',
          outlineOffset: '0px',
          ['--outline-style' as string]: outlineStyle,
          ...(hasClarifyingQuestions && {
            ['--pulse-border-color' as string]: 'var(--orange)',
          }),
        }}
        className={cn(
          'group rounded',
          '[outline:var(--outline-style)]',
          isAiThinkingForChat &&
            !hasClarifyingQuestions &&
            'animate-pulse-border',
          !isAiThinkingForChat &&
            !hasPartialSelection &&
            !(isInSelectMode && isActiveFrame) &&
            'group-hover:[outline:calc(2px*var(--inverse-zoom,1))_solid_var(--primary-hex)]',
        )}
      >
        {isAiThinkingForChat && <div className='absolute inset-0 z-[100]' />}
        <div
          id={`screen-id-${chatId}`}
          data-mobile-frame={isActiveFrame || undefined}
        >
          {genType === 'web' ? (
            <Suspense>
              <WebFrame
                flowId={flowId}
                chatId={chatId}
                activeStructure={activeStructure}
              />
            </Suspense>
          ) : (
            <WebHtmlCssFrame
              flowId={flowId}
              chatId={chatId}
              genType={genType}
              activeStructure={activeStructure}
              isActiveFrame={isActiveFrame}
            />
          )}
        </div>
      </div>
      {!readonly && mode !== 'pan' && (
        <SelectionOverlays
          containerRef={overlayContainerRef}
          chatId={chatId}
          hasPartialSelection={hasPartialSelection}
          isInSelectMode={isInSelectMode}
          isActiveFrame={isActiveFrame}
          isSelected={isSelected}
          isFrameNodeHovered={isFrameNodeHovered}
        />
      )}
    </div>
  );
};

function getOutlineStyle(
  readonly: boolean,
  isAiThinking: boolean,
  hasClarifyingQuestions: boolean,
  hasPartialSelection: boolean,
  isActiveFrame: boolean,
  isSelected: boolean,
): string {
  if (!readonly && isAiThinking) {
    const color = hasClarifyingQuestions ? 'var(--orange)' : 'var(--purple)';
    return `calc(2px * var(--inverse-zoom, 1)) solid ${color}`;
  }
  if (!readonly && hasPartialSelection) {
    // When something is selected inside, the inner overlay is the single
    // source of selection visual — fall back to the default gray frame border.
    return 'calc(1px * var(--inverse-zoom, 1)) solid var(--border)';
  }
  if (isActiveFrame || isSelected) {
    return 'calc(1px * var(--inverse-zoom, 1)) solid var(--primary-hex)';
  }
  return 'calc(1px * var(--inverse-zoom, 1)) solid var(--border)';
}

/**
 * Lightweight placeholder that matches frame dimensions but renders no heavy content.
 * Used while data is loading or when the screen is virtualized.
 */
function SuspendedFrame({
  genType,
  outlineStyle,
  isAiThinking,
  hasClarifyingQuestions,
}: {
  genType: GenType;
  outlineStyle: string;
  isAiThinking: boolean;
  hasClarifyingQuestions: boolean;
}) {
  const {width, height} = getNodeDimensions(genType);

  return (
    <div
      style={{
        width,
        minHeight: height,
        outlineOffset: '0px',
        ['--outline-style' as string]: outlineStyle,
      }}
      className={cn(
        'rounded bg-background',
        '[outline:var(--outline-style)]',
        isAiThinking && !hasClarifyingQuestions && 'animate-pulse-border',
      )}
    />
  );
}

export const Frame = memo(FrameComponent);
